/**
 * "Net cash" — `mockups/studio/money.html` §net cash.
 *
 * Every figure in the table is a field on `MoneyOutView`: `collected`,
 * `delivery_and_running`, `draw`, `total`, `net_cash`, `opening_balance`,
 * `closing_balance`. The arithmetic that reconciles them was done by
 * `readmodels/money-out.ts` — this is CONTRACT.md §2.9's arithmetic build-up, which is
 * a table with `Tr variant="sub"` for a component and `variant="total"` for a sum, and
 * §5 refused a `.ladder` for exactly this.
 *
 * **The two subtracted lines print as they are.** `delivery_and_running` and `draw`
 * are positive amounts that come *off* the cash in, and the mockup prints them with a
 * minus. Negating them here would be arithmetic on a `Money`; the column header and
 * the "Cash out" line below them are what say which way they go, and the row label
 * says it too.
 *
 * The mockup's ochre notice about the SEO suite's annual renewal is **not** here:
 * "nobody has decided" is a judgement about a subscription, and no field carries it.
 */

import type { MoneyOutView, StudioMoneyView } from '@fareast/shared'

import { Card, CardBody } from '../../../components/portal/Card.js'
import { Fold } from '../../../components/portal/Disclosure.js'
import { Grid, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Answer, B, DayText, More, Muted, Num, Prose } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'

export type NetCashSectionProps = {
  readonly out: MoneyOutView
  readonly firstDay: string
  readonly lastDay: string
  /** The Wise queue, for the "what is not counted" fold. */
  readonly queue: StudioMoneyView['wise_queue']
}

export function NetCashSection({ out, firstDay, lastDay, queue }: NetCashSectionProps) {
  const format = useFormat()

  return (
    <Section>
      <SectionHead title="Net cash" note="Money in minus money out, and where the account stands" />

      <Answer>
        {`${format.monthLong(out.period)} kept `}
        <B>
          <Num value={out.net_cash} />
        </B>
        {' after everything, the draw included.'}
      </Answer>

      <Grid cols="2-1" top>
        <Card>
          <CardBody tight scrollX>
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Line</Th>
                  <Th scope="col">What it is</Th>
                  <Th scope="col" right>
                    Amount
                  </Th>
                </Tr>
              </THead>
              <TBody>
                <Tr>
                  <Td>
                    <B>Cash in</B>
                  </Td>
                  <Td>
                    <Muted>Collected against this month&rsquo;s invoices, all rails</Muted>
                  </Td>
                  <Td right num>
                    <Num value={out.collected} />
                  </Td>
                </Tr>
                <Tr variant="sub">
                  <Td>Delivery and running the studio, off</Td>
                  <Td>
                    <Muted>Contractors, tooling, bookkeeping, rails</Muted>
                  </Td>
                  <Td right num>
                    <Num value={out.delivery_and_running} />
                  </Td>
                </Tr>
                <Tr variant="sub">
                  <Td>Owner draw, off</Td>
                  <Td>
                    <Muted>Fixed monthly</Muted>
                  </Td>
                  <Td right num>
                    <Num value={out.draw} />
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <B>Cash out</B>
                  </Td>
                  <Td>
                    <Muted>The two lines above</Muted>
                  </Td>
                  <Td right num>
                    <Num value={out.total} />
                  </Td>
                </Tr>
                <Tr variant="total">
                  <Td colSpan={2}>{`Net cash, ${format.monthLong(out.period)}`}</Td>
                  <Td right num>
                    <Num value={out.net_cash} lg />
                  </Td>
                </Tr>

                <Tr variant="head">
                  <Td colSpan={3}>The account</Td>
                </Tr>
                <Tr>
                  <Td>Opening balance</Td>
                  <Td date>
                    <DayText value={firstDay} />
                  </Td>
                  <Td right num>
                    {out.opening_balance === null ? (
                      <Muted>not set</Muted>
                    ) : (
                      <Num value={out.opening_balance} />
                    )}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{`Net cash, ${format.month(out.period)}`}</Td>
                  <Td>
                    <Muted>As above</Muted>
                  </Td>
                  <Td right num>
                    <Num value={out.net_cash} />
                  </Td>
                </Tr>
                <Tr variant="total">
                  <Td>Closing balance</Td>
                  <Td date>
                    <DayText value={lastDay} />
                  </Td>
                  <Td right num>
                    {out.closing_balance === null ? (
                      <Muted>no opening balance to add it to</Muted>
                    ) : (
                      <Num value={out.closing_balance} lg />
                    )}
                  </Td>
                </Tr>
              </TBody>
            </Table>
          </CardBody>
        </Card>

        <Stack>
          <Fold summary="What is not counted in the position">
            <Prose>
              {`The ${String(queue.length)} ${queue.length === 1 ? 'credit' : 'credits'} in the Wise review queue are real money sitting in the balance, but none of it is allocated to an invoice yet, so it is held out of both halves. It lands in the position the day a person matches it. `}
              <More href="#wise">The queue →</More>
            </Prose>
            <Prose>
              This is cash, not accounting. Nothing here is accrued, deferred or depreciated: a
              payment is in the month it left, and what the month has not collected is not a cost —
              it is simply not here yet.
            </Prose>
          </Fold>
        </Stack>
      </Grid>
    </Section>
  )
}
